// Kanban board generico con drag-and-drop (@dnd-kit)
// - Scroll verticale per colonne con tante card (touch-pan-y sulle card)
// - Scroll orizzontale centrato (con padding laterale per centrare prima/ultima colonna)
// - Tab bar in cima per navigazione rapida tra sezioni
// - Altezza flex dinamica (niente magic number calc)
import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { KanbanColumnDef } from '@/types';
import { cn } from '@/utils';
import { Plus } from 'lucide-react';

interface KanbanItem {
  id: string;
  status: string;
  position: number;
}

interface KanbanBoardProps<T extends KanbanItem> {
  columns: KanbanColumnDef<T>[];
  items: T[];
  onMove: (id: string, status: T['status'], position: number) => void | Promise<void>;
  onReorder: (status: T['status'], orderedIds: string[]) => void | Promise<void>;
  onEdit?: (item: T) => void;
  onAdd?: (status: T['status']) => void;
  renderCard: (item: T, listeners?: any) => React.ReactNode;
  getKey?: (item: T) => string;
}

export function KanbanBoard<T extends KanbanItem>({
  columns,
  items,
  onMove,
  onReorder,
  onEdit,
  onAdd,
  renderCard,
}: KanbanBoardProps<T>) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeColumn, setActiveColumn] = useState<string>(columns[0]?.status ?? '');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Activation constraints severi per evitare drag accidentali
  // - Touch: delay 250ms + tolerance 8px → permette scroll immediato, drag solo dopo long-press
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const itemsByColumn = useMemo(() => {
    const map = new Map<string, T[]>();
    for (const col of columns) {
      map.set(
        col.status,
        items
          .filter((i) => i.status === col.status)
          .sort((a, b) => a.position - b.position),
      );
    }
    return map;
  }, [items, columns]);

  const activeItem = activeId ? items.find((i) => i.id === activeId) ?? null : null;

  function onDragStart(e: DragStartEvent) {
    setActiveId(String(e.active.id));
  }

  function onDragOver(e: DragOverEvent) {
    const { active, over } = e;
    if (!over) return;
    const activeIdStr = String(active.id);
    const overId = String(over.id);

    const fromCol = columns.find((c) => itemsByColumn.get(c.status)?.some((i) => i.id === activeIdStr));
    let toCol = columns.find((c) => c.status === overId);
    if (!toCol) {
      toCol = columns.find((c) => itemsByColumn.get(c.status)?.some((i) => i.id === overId));
    }
    if (!fromCol || !toCol || fromCol.status === toCol.status) return;

    const dest = itemsByColumn.get(toCol.status) ?? [];
    const newPos = dest.length;
    void onMove(activeIdStr, toCol.status as T['status'], newPos);
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    setActiveId(null);
    if (!over) return;
    const activeIdStr = String(active.id);
    const overId = String(over.id);

    let toCol = columns.find((c) => c.status === overId);
    if (!toCol) {
      toCol = columns.find((c) => itemsByColumn.get(c.status)?.some((i) => i.id === overId));
    }
    if (!toCol) return;

    const colItems = itemsByColumn.get(toCol.status) ?? [];
    const withoutActive = colItems.filter((i) => i.id !== activeIdStr);
    let insertIndex = withoutActive.length;
    if (overId !== toCol.status) {
      const overIdx = withoutActive.findIndex((i) => i.id === overId);
      if (overIdx >= 0) insertIndex = overIdx;
    }
    const reordered = [...withoutActive];
    const movedItem = items.find((i) => i.id === activeIdStr);
    if (movedItem) reordered.splice(insertIndex, 0, movedItem);
    const orderedIds = reordered.map((i) => i.id);
    void onReorder(toCol.status as T['status'], orderedIds);
  }

  // ------------------------------------------------------------
  // Scroll orizzontale centrato: scrolla la colonna al centro della vista
  // ------------------------------------------------------------
  const scrollToColumn = useCallback((status: string, smooth = true) => {
    const container = scrollContainerRef.current;
    const target = columnRefs.current.get(status);
    if (!container || !target) return;

    const containerWidth = container.clientWidth;
    const targetLeft = target.offsetLeft;
    const targetWidth = target.offsetWidth;
    const scrollLeft = targetLeft - (containerWidth - targetWidth) / 2;

    container.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: smooth ? 'smooth' : 'auto',
    });
    setActiveColumn(status);
  }, []);

  // Rileva quale colonna è visibile al centro durante lo scroll manuale
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const containerCenter = container.scrollLeft + container.clientWidth / 2;
    let closest: { status: string; distance: number } | null = null;
    for (const col of columns) {
      const el = columnRefs.current.get(col.status);
      if (!el) continue;
      const elCenter = el.offsetLeft + el.offsetWidth / 2;
      const distance = Math.abs(elCenter - containerCenter);
      if (!closest || distance < closest.distance) {
        closest = { status: col.status, distance };
      }
    }
    if (closest) setActiveColumn(closest.status);
  }, [columns]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(handleScroll);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [handleScroll]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      {/* ROOT: flex column che riempie l'altezza del parent (flex-1, no h-full) */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* TAB BAR di navigazione sezioni */}
        <ColumnTabBar
          columns={columns}
          itemsByColumn={itemsByColumn}
          activeColumn={activeColumn}
          onSelect={(status) => scrollToColumn(status)}
        />

        {/* CONTAINER SCROLL ORIZZONTALE — flex-1 per riempire lo spazio */}
        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 flex items-stretch gap-3 overflow-x-auto overflow-y-hidden pb-4 no-scrollbar"
          style={{
            scrollSnapType: 'x proximity',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
            // Padding laterale per centrare prima/ultima colonna
            paddingLeft: 'calc((100vw - min(80vw, 18rem)) / 2)',
            paddingRight: 'calc((100vw - min(80vw, 18rem)) / 2)',
          }}
        >
          {columns.map((col) => {
            const colItems = itemsByColumn.get(col.status) ?? [];
            return (
              <div
                key={col.id}
                ref={(el) => {
                  if (el) columnRefs.current.set(col.status, el);
                  else columnRefs.current.delete(col.status);
                }}
                className="shrink-0 w-[80vw] sm:w-72 flex"
                style={{ scrollSnapAlign: 'center' }}
              >
                <KanbanColumn
                  column={col}
                  count={colItems.length}
                  onAdd={onAdd ? () => onAdd(col.status as T['status']) : undefined}
                >
                  <ColumnDropArea colId={col.status}>
                    <div className="flex flex-col gap-2 min-h-[60px]">
                      {colItems.length === 0 && (
                        <div className="text-center text-xs text-slate-400 dark:text-slate-500 py-6 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                          Trascina qui
                        </div>
                      )}
                      {colItems.map((item) => (
                        <SortableCard
                          key={item.id}
                          id={item.id}
                          onEdit={onEdit ? () => onEdit(item) : undefined}
                          renderCard={renderCard}
                          item={item}
                        />
                      ))}
                    </div>
                  </ColumnDropArea>
                </KanbanColumn>
              </div>
            );
          })}
        </div>
      </div>

      <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
        {activeItem ? (
          <div className="opacity-90 rotate-2 scale-105 shadow-2xl">
            {renderCard(activeItem)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ------------------------------------------------------------
// Tab bar orizzontale per navigare le sezioni
// ------------------------------------------------------------
function ColumnTabBar<T extends KanbanItem>({
  columns,
  itemsByColumn,
  activeColumn,
  onSelect,
}: {
  columns: KanbanColumnDef<T>[];
  itemsByColumn: Map<string, T[]>;
  activeColumn: string;
  onSelect: (status: string) => void;
}) {
  const tabRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  // Scrolla la tab attiva nella vista
  useEffect(() => {
    if (activeTabRef.current && tabRef.current) {
      const tab = activeTabRef.current;
      const container = tabRef.current;
      const tabLeft = tab.offsetLeft;
      const tabWidth = tab.offsetWidth;
      const containerWidth = container.clientWidth;
      const scrollLeft = tabLeft - (containerWidth - tabWidth) / 2;
      container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
    }
  }, [activeColumn]);

  return (
    <div
      ref={tabRef}
      className="flex gap-2 overflow-x-auto no-scrollbar px-4 pb-2 pt-1 shrink-0"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {columns.map((col) => {
        const count = itemsByColumn.get(col.status)?.length ?? 0;
        const isActive = activeColumn === col.status;
        return (
          <button
            key={col.id}
            ref={isActive ? activeTabRef : null}
            onClick={() => onSelect(col.status)}
            className={cn(
              'shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all',
              isActive
                ? 'bg-brand-600 text-white shadow-sm shadow-brand-600/30'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
            )}
          >
            <span className={cn('w-1.5 h-1.5 rounded-full', isActive ? 'bg-white' : col.color)} />
            {col.title}
            <span
              className={cn(
                'ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] flex items-center justify-center font-bold',
                isActive ? 'bg-white/25 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
              )}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------
// Colonna — h-full per riempire il wrapper, body scrollabile con min-h-0
// ------------------------------------------------------------
function KanbanColumn<T extends KanbanItem>({
  column,
  count,
  children,
  onAdd,
}: {
  column: KanbanColumnDef<T>;
  count: number;
  children: React.ReactNode;
  onAdd?: () => void;
}) {
  return (
    // flex-1 per riempire il wrapper (che è flex items-stretch), min-h-0 per scroll
    <div className="flex-1 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 flex flex-col min-h-0">
      {/* Header colonna (fisso) */}
      <div className="flex items-center justify-between px-3 py-2.5 shrink-0 border-b border-slate-200/60 dark:border-slate-700/60">
        <div className="flex items-center gap-2 min-w-0">
          <span className={cn('w-2 h-2 rounded-full shrink-0', column.color)} />
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">{column.title}</h3>
          <span className="text-xs font-medium text-slate-400 bg-slate-200/60 dark:bg-slate-700/60 rounded-full px-1.5 py-0.5 min-w-[20px] text-center shrink-0">
            {count}
          </span>
        </div>
        {onAdd && (
          <button
            onClick={onAdd}
            className="p-1 rounded-md text-slate-400 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/30 transition-colors shrink-0"
            title="Aggiungi"
          >
            <Plus size={16} />
          </button>
        )}
      </div>
      {/* Body scrollabile verticalmente — min-h-0 è cruciale per flex overflow */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2.5 py-2.5 no-scrollbar">
        {children}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Drop area della colonna
// ------------------------------------------------------------
function ColumnDropArea({ colId, children }: { colId: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: colId });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-xl transition-colors min-h-full',
        isOver && 'bg-brand-50/60 dark:bg-brand-900/20 ring-2 ring-brand-300/50 dark:ring-brand-700/50',
      )}
    >
      {children}
    </div>
  );
}

// ------------------------------------------------------------
// Card sortable
// ------------------------------------------------------------
function SortableCard<T extends KanbanItem>({
  id,
  item,
  renderCard,
  onEdit,
}: {
  id: string;
  item: T;
  renderCard: (item: T, listeners?: any) => React.ReactNode;
  onEdit?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
  };

  const wrappedListeners = {
    ...listeners,
    onPointerDown: (e: React.PointerEvent) => {
      if (listeners?.onPointerDown) listeners.onPointerDown(e);
    },
    onTouchStart: (e: React.TouchEvent) => {
      if (listeners?.onTouchStart) listeners.onTouchStart(e);
    },
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...wrappedListeners}
      // touch-pan-y: permette scroll verticale touch (pan-y) ma blocca pan orizzontale
      // (che confliggerebbe con il drag). Il drag si attiva solo dopo long-press 250ms
      // grazie all'activation constraint del TouchSensor.
      className={cn('touch-pan-y select-none', isDragging && 'opacity-40')}
      onDoubleClick={onEdit}
    >
      {renderCard(item, wrappedListeners)}
    </div>
  );
}
